import React, { useContext, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  TextTitle,
  ValidatedPanel,
  getPanelErrorProps,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import {
  entityIs,
  propertyIs,
  andComp,
} from '@bingads-webui-campaign-react/form-error';
import { useResponsiveStyles } from '@bingads-webui-campaign-react/common-hooks';
import { SectionShimmer, withShimmer } from '../shimmers';

import { ERROR_ENTITY } from '../../constants';
import { ValidatedBidValue } from '../sections/bid-value';

const getBidStrategyStyles = () => ({
  budgetTitle: {
    fontSize: '18px',
    display: 'inline',
  },
  bottomMargin: {
    marginBottom: '8px',
  },
});

const entityIsCampaign = entityIs(ERROR_ENTITY.Campaign);
const bidValueProp = andComp(entityIsCampaign, propertyIs('BiddingScheme'));
const ServerValidatedBidValueComponentWithShimmer =
  withShimmer(
    withUnvalidatedControlErrors(
      ValidatedBidValue,
      { filter: bidValueProp }
    ),
    SectionShimmer
  );

export const BidValuePanel = (props) => {
  const i18n = useContext(I18nContext);
  const getStyles = useResponsiveStyles(getBidStrategyStyles);

  const {
    key,
    isLoading,
    expand,
    onDataChange,
    data = {},
    bidValueType,
    ...rest
  } = props;

  const {
    campaign,
    currency,
  } = data;

  const bidAmount = useMemo(() => {
    if (!isLoading && campaign?.BiddingScheme) {
      const { BiddingScheme } = campaign;
      return BiddingScheme[bidValueType];
    }
    return null;
  }, [isLoading, campaign, bidValueType]);

  const bidValueDetails = useMemo(() => i18n.getString(_TL_('{{bidAmount}} {{currency}}'), { bidAmount, currency }), [bidAmount, currency, i18n]);

  const onBidValueChange = useCallback((updatedData) => {
    onDataChange({
      data: prevData => ({
        ...prevData,
        [bidValueType]: updatedData,
      }),
    });
  }, [onDataChange, bidValueType]);

  return (
    <ValidatedPanel
      key={key}
      title={
        <TextTitle
          // icon={BidStrategyImg}
          titleKey={_TL_('Bid value')}
          // topicKey="pop_BA_PMAX_BidStrategy"
          as="h2"
          headingClassName={classNames(getStyles('budgetTitle'))}
          details={bidValueDetails}
        />
        }
      actuationProps={{
          expandKey: key,
          expand,
        }}
      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      {...rest}
    >
      <ServerValidatedBidValueComponentWithShimmer
        showShimmer={isLoading}
        validationProps={{ data: campaign, bidValueType }}
        onBidChange={onBidValueChange}
      />
    </ValidatedPanel>
  );
};

BidValuePanel.propTypes = {
  key: PropTypes.string.isRequired,
  onDataChange: PropTypes.func.isRequired,
  data: PropTypes.shape({
    currency: PropTypes.string,
    campaign: PropTypes.shape({}),
  }),
  isLoading: PropTypes.bool,
  expand: PropTypes.func.isRequired,
  bidValueType: PropTypes.string.isRequired,
};

BidValuePanel.defaultProps = {
  data: {},
  isLoading: false,
};
